import './styles.scss';

import { Box } from '@mui/system';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { SUBSCRIPTIONS_KEY } from '../../../../constants/websocket-events';
import { CurrencyTypeEnum } from '../../../../model/currency_type.enum';
import { GlobalContext } from '../../../../providers/global-provider';
import { TokenDataRequest } from '../../../../providers/model/interfaces/token_data';
import { WebSocketContext } from '../../../../providers/websocket-context/websocket-provider';
import Utils from '../../../../utils/immersive_utils';

import { DataLabelEnum } from '../../../../model/data_label.enum';
import TokenSelector from './token-selector';
import { CircularProgress } from '@mui/material';
import { getPriceDecimalsByMagnitude } from '../../../../utils/decimal-map';

const TOKEN_DATA_ID = SUBSCRIPTIONS_KEY.TOKEN_DATA;

type TokenDataProps = {
  currency: CurrencyTypeEnum;
  setCurrency?: React.Dispatch<React.SetStateAction<CurrencyTypeEnum>>;
};

const toPercent = (value: number) => `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
const toMPercent = (value: number) => `${value >= 0 ? '+' : ''}${(100 * value).toFixed(3)}%`;

export const TokenDataContent = ({ currency, setCurrency }: TokenDataProps) => {
  const {
    subscribeToChannel,
    unsubscribeFromChannel: unsubscribe,
    isConnected,
    sendMessage,
  } = useContext(WebSocketContext);
  const { marketDataOverviewCurrentToken } = useContext(GlobalContext);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const previousDataDeribitRef = useRef<any>(null);
  const [lastestUpdate, setLastestUpdate] = useState<Date>(new Date());

  const currentSubscribeId = useRef<string>('');

  const isGraphGallery = !!setCurrency;
  const isSameTab = marketDataOverviewCurrentToken === currency;

  const isCurrencyBitcoinOrEthereum =
    currency === CurrencyTypeEnum.BITCOIN || currency === CurrencyTypeEnum.ETHEREUM;

  const generateCompositeId = (id: string) => {
    if (isGraphGallery) {
      return `gallery_${id}`;
    }

    return `${id}_${currency}`;
  };

  const isObjectEqual = (obj1: any, obj2: any) => {
    if (!obj1 || !obj2) return false;

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    if (keys1.length !== keys2.length) return false;
    for (const key of keys1) {
      if (obj1[key] !== obj2[key]) return false;
    }
    return true;
  };

  const handleResponse = async (response: any) => {
    if (!response || !response.method) return;

    const {
      params: {
        data: {
          deribit: { stats, index_price, open_interest, funding_8h },
        },
      },
    } = response;

    const { high, low, price_change, volume_usd } = stats;

    const newDataDeribit = {
      index_price,
      open_interest,
      funding_8h,
      high,
      low,
      price_change,
      volume_usd,
    };

    if (isObjectEqual(previousDataDeribitRef.current, newDataDeribit)) return;

    previousDataDeribitRef.current = newDataDeribit;
    setLoading(false);
    setData(newDataDeribit);
    setLastestUpdate(new Date());
  };

  const reqTokenData = useCallback(() => {
    const tokenRequest = new TokenDataRequest(
      generateCompositeId(TOKEN_DATA_ID),
      currency,
      DataLabelEnum.PERPETUAL,
    );

    currentSubscribeId.current = generateCompositeId(TOKEN_DATA_ID);

    sendMessage(JSON.stringify(tokenRequest));
    subscribeToChannel(currentSubscribeId.current, handleResponse);
  }, [sendMessage, subscribeToChannel, currency]);

  const newTokenSubscription = useCallback(() => {
    const unsubId = currentSubscribeId.current;

    if (unsubId) {
      unsubscribe(unsubId);
    }

    unsubscribe(unsubId);
    setLoading(true);
    reqTokenData();
  }, [unsubscribe, reqTokenData, loading, currency]);

  const toFormatDecimals = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const toHighAndLowData = (value: number) => {
    if (isCurrencyBitcoinOrEthereum) {
      return toFormatDecimals(value);
    }

    return currency === CurrencyTypeEnum.SOL
      ? Utils.formatNumber(value, 2)
      : Utils.formatNumber(value, 4);
  };

  const TOKEN_DATA_ITEMS = [
    {
      label: '24h Change',
      key: 'price_change',
      class: 'token-data-colorful token-hidden-mobile',
      fn: toPercent,
    },
    { label: '24h High', key: 'high', fn: toHighAndLowData },
    { label: '24h Low', key: 'low', fn: toHighAndLowData },
    { label: '24h Volume (USDC)', key: 'volume_usd', fn: toFormatDecimals },
    { label: 'Open (USDC)', key: 'open_interest', fn: toFormatDecimals },
    { label: 'Funding/8h', key: 'funding_8h', class: 'token-data-colorful', fn: toMPercent },
  ];

  const handleIndexPriceCurrency = () => {
    if (!data) return;

    const indexPrice = data.index_price;

    // Magnitude-based precision now lives in one shared helper so this header
    // and the exotics pricer are provably driven by the same ladder. Output is
    // unchanged for non-negative index prices.
    return Utils.valueToCurrency(indexPrice, getPriceDecimalsByMagnitude(indexPrice));
  };

  const renderTokenDataItems = () => {
    if (!data) return;

    return TOKEN_DATA_ITEMS.map((item) => (
      <div key={item.key} className={`token-data-item ${item.class || ''}`}>
        <span className="label">{item.label}</span>
        <span className={`value ${data[item.key] > 0 ? 'value-green' : 'value-red'}`}>
          {item.fn ? item.fn(data[item.key]) : data[item.key]}
        </span>
      </div>
    ));
  };

  useEffect(() => {
    if (!isConnected) return;

    if (isSameTab || isGraphGallery) {
      setData(null);
      newTokenSubscription();
    }
  }, [isConnected, isSameTab, currency]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (lastestUpdate) {
        const now = Date.now();

        if (now - lastestUpdate.getTime() > 10000) {
          setLoading(true);
          newTokenSubscription();
        }
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [lastestUpdate, currency]);

  return (
    <Box className="token-data-container">
      <div className="token-selector-currency">
        <TokenSelector currency={currency} setCurrency={setCurrency} />
        {!loading && !!data && (
          <div className="token-currency-container">
            <span className="token-currency">{handleIndexPriceCurrency()}</span>
            <span
              className={`token-data-colorful ${data.price_change > 0 ? 'value-green' : 'value-red'}`}
            >
              {toPercent(data.price_change)}
            </span>
          </div>
        )}
      </div>
      {!isConnected || loading ? (
        <div style={{ margin: 'auto' }}>
          <CircularProgress size={34} />
        </div>
      ) : (
        <div className="token-data-items">{renderTokenDataItems()}</div>
      )}
    </Box>
  );
};
